import type { JsonValue } from '@components/json-editor';

import type { IntegrationEndpointVariable } from '../integrations-types';

export type MultipartField = { key: string };

// Matches a repeatable-object key like `signers[0][email_address]` — the
// multipart shape for an array of objects. A bare `files[0]` stays literal.
const ARRAY_FIELD_PATTERN = /^([^[\]]+)\[\d+\]\[([^[\]]+)\]$/;

// Build the example request body from the flat field keys. `signers[0][name]`
// keys collapse into one array group; every other key stays a flat field.
export const buildMultipartExampleData = (
  fields: MultipartField[],
  getExample: (key: string) => string,
  legacyBases: Set<string> = new Set()
): { [key: string]: JsonValue } => {
  const exampleData: { [key: string]: JsonValue } = {};
  const arrayTemplates: { [base: string]: { [subField: string]: string } } = {};

  fields.forEach((field) => {
    if (!field.key) {
      return;
    }
    const match = field.key.match(ARRAY_FIELD_PATTERN);
    // Legacy flat bracket bases stay literal (see getLiteralBracketBases).
    if (!match || legacyBases.has(match[1])) {
      // A flat key that collides with an array group already claiming this
      // name is dropped rather than clobbering the group.
      if (!(field.key in arrayTemplates)) {
        exampleData[field.key] = getExample(field.key);
      }
      return;
    }
    const [, base, subField] = match;
    // A group whose name is already taken by a flat field is dropped rather
    // than overwriting it; first field to claim the name wins.
    if (base in exampleData && !arrayTemplates[base]) {
      return;
    }
    if (!arrayTemplates[base]) {
      arrayTemplates[base] = {};
      // Keep one template item by reference so later sub-fields land here too.
      exampleData[base] = [arrayTemplates[base]];
    }
    arrayTemplates[base][subField] = getExample(field.key);
  });

  return exampleData;
};

// Bases already made of flat (non-array) bracket variables — e.g. a legacy
// endpoint's hand-entered signers[0][name], signers[1][name]. Kept literal on
// save so existing variables (and their action mappings) aren't restructured.
export const getLiteralBracketBases = (variables: IntegrationEndpointVariable[]): Set<string> => {
  const bases = new Set<string>();
  variables.forEach((variable) => {
    if (variable.type === 'array') {
      return;
    }
    const base = variable.name.match(ARRAY_FIELD_PATTERN)?.[1];
    if (base) {
      bases.add(base);
    }
  });
  return bases;
};

// Expand saved variables into the editor's flat keys: an array variable
// becomes one `base[0][subField]` key per template field.
export const variablesToMultipartFields = (
  variables: IntegrationEndpointVariable[]
): MultipartField[] =>
  variables.flatMap((variable) => {
    if (variable.type === 'array') {
      // An array with no template can't be shown as bracket fields; skip it
      // rather than misrepresenting it as (and saving it as) a flat text field.
      if (!variable.template?.length) {
        return [];
      }
      return variable.template.map((templateVariable) => ({
        key: `${variable.name}[0][${templateVariable.name}]`,
      }));
    }
    return [{ key: variable.name }];
  });
